import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import React, { useCallback, useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import { getAccessToken } from "@/helpers/auth-helpers";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useLoading from "@/hooks/use-loading";
import { WhatsappBspOnboardingLinkMessages } from "@/messages/platforms/whatsappBspConnection/whatsappBspOnboardingLinkMessages";
import customerPlatformService from "@/services/customer-platform.service";
import ICustomer from "@/interfaces/customer.interfaces";
import customerService from "@/services/customer.service";

interface Props {
  messages: WhatsappBspOnboardingLinkMessages;
  onOnboardingComplete: (appId: string, phoneNumber: string, appName: string) => void;
}

// Polling automático removido - verificação apenas manual via botão 

export function OnboardingLink({ messages, onOnboardingComplete }: Props) {
  const [embedLink, setEmbedLink] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string>("");
  const [customerData, setCustomerData] = useState<ICustomer | null>(null);
  const [isLoadingCustomerData, setIsLoadingCustomerData] = useState(true);

  const customerId = useGetCustomerId();
  const accessToken = getAccessToken();
  const { showToast } = useToast();
  const { isLoading, startLoading, endLoading } = useLoading();

  // Refs de polling removidos - não há mais polling automático

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!customerId) {
        setIsLoadingCustomerData(false);
        return;
      }

      try {
        setIsLoadingCustomerData(true);
        
        const customerIdNum = parseInt(customerId, 10);
        if (!isNaN(customerIdNum)) {
          const customer = await customerService.getCustomerById(customerIdNum);
          setCustomerData(customer);
        }
      } catch (error) {
        
      } finally {
        setIsLoadingCustomerData(false);
      }
    };

    fetchCustomerData();
  }, [customerId]);

  // useEffect de cleanup removido - não há mais polling automático

  // checkOnboardingStatus removido - verificação apenas manual via botão

  const handleStartOnboarding = useCallback(async () => {
    if (!customerId || !accessToken) {
      setError("Dados de autenticação não disponíveis");
      showToast({
        duration: 10000,
        text: "Dados de autenticação não disponíveis",
        type: "error",
      });
      return;
    }

    let customer = customerData;
    if (!customer) {
      try {
        const customerIdNum = parseInt(customerId, 10);
        if (!isNaN(customerIdNum)) {
          customer = await customerService.getCustomerById(customerIdNum);
          setCustomerData(customer);
        }
      } catch (error) {
        setError("Erro ao carregar dados do cliente");
        showToast({
          duration: 10000,
          text: "Erro ao carregar dados do cliente",
          type: "error",
        });
        return;
      }
    }

    if (!customer) {
      setError("Dados do cliente não disponíveis");
      return;
    }

    try {
      startLoading();
      setError("");

      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write("<html><body><p>Carregando...</p></body></html>");
      }

      const onboardingResponse = await customerPlatformService.gupshupOnboarding({
        customerId,
        customerData: {
          email: customer.email || "",
          nomeFantasia: customer.fantasy || customer.name || "",
          telefone: customer.phone1 || customer.mobilePhone || "",
        },
      });

      const newAppId = onboardingResponse.appId;
      setAppId(newAppId);
      setEmbedLink(onboardingResponse.embedLink || "");

      if (onboardingResponse.embedLink && newWindow) {
        newWindow.location.href = onboardingResponse.embedLink;
      } else if (onboardingResponse.embedLink) {
        window.open(onboardingResponse.embedLink, "_blank");
      }

      // Verificar se já está ativo imediatamente após criar
      if (onboardingResponse.status === "active") {
        try {
          const appDetails = await customerPlatformService.gupshupCheckStatus(newAppId);
          if (appDetails.status === "active" && appDetails.phoneNumber) {
            setIsOnboardingComplete(true);
            setIsChecking(false);
            onOnboardingComplete(newAppId, appDetails.phoneNumber, appDetails.appName);
            return;
          }
        } catch (error) {
          // Continuar normalmente se não conseguir verificar
        }
      }

      // Não inicia polling automático - usuário deve clicar no botão para verificar
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : messages.whatsappBspOnboardingLinkError;
      setError(errorMessage);
      showToast({
        duration: 10000,
        text: errorMessage,
        type: "error",
      });
    } finally {
      endLoading();
    }
  }, [
    customerId,
    accessToken,
    customerData,
    startLoading,
    endLoading,
    messages.whatsappBspOnboardingLinkError,
    showToast,
    onOnboardingComplete,
  ]);

  const handleVerifyAndContinue = useCallback(async () => {
    if (!appId) {
      showToast({
        duration: 10000,
        text: "App ID não disponível. Por favor, tente novamente.",
        type: "error",
      });
      return;
    }

    try {
      startLoading();
      const status = await customerPlatformService.gupshupCheckStatus(appId);

      if (status.status === "active" && status.phoneNumber) {
        setIsOnboardingComplete(true);
        setIsChecking(false);
        onOnboardingComplete(appId, status.phoneNumber, status.appName);
      } else {
        showToast({
          duration: 10000,
          text: "Onboarding ainda não foi completado. Por favor, complete o cadastro no portal GupShup.",
          type: "warning",
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao verificar status do onboarding";
      showToast({
        duration: 10000,
        text: errorMessage,
        type: "error",
      });
    } finally {
      endLoading();
    }
  }, [appId, onOnboardingComplete, showToast, startLoading, endLoading]);

  return (
    <MDBox>
      <MDBox width="82%" textAlign="left" mx="auto" my={4}>
        <MDBox mb={1}>
          <MDTypography variant="h5" fontWeight="regular">
            {messages.whatsappBspOnboardingLinkText1}
          </MDTypography>
        </MDBox>
        <MDTypography variant="body2" color="text" mb={3}>
          {messages.whatsappBspOnboardingLinkText2}
        </MDTypography>

        {!embedLink && (
          <MDBox>
            <MDButton
              variant="gradient"
              color="dark"
              onClick={handleStartOnboarding}
              isLoading={isLoading || isLoadingCustomerData}
              disabled={isLoading || isLoadingCustomerData || !customerId || !accessToken}
            >
              {messages.whatsappBspOnboardingLinkButton}
            </MDButton>
            {isLoadingCustomerData && (
              <MDTypography variant="body2" color="text" mt={1}>
                Carregando dados do cliente...
              </MDTypography>
            )}
          </MDBox>
        )}

        {appId && (
          <MDBox>
            {embedLink && (
              <MDBox mb={2}>
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={() => window.open(embedLink, "_blank")}
                >
                  {messages.whatsappBspOnboardingLinkButton}
                </MDButton>
              </MDBox>
            )}

            <MDTypography variant="body2" color="text" mb={2}>
              {messages.whatsappBspOnboardingLinkChecking}
            </MDTypography>

            {!embedLink && (
              <MDTypography variant="body2" color="warning" mb={2}>
                Link máximo de onboarding já foi enviado. Você pode verificar o status e avançar se o onboarding já foi completado.
              </MDTypography>
            )}

            <MDBox mt={3} display="flex" justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="success"
                onClick={handleVerifyAndContinue}
                disabled={isLoading || !appId}
                isLoading={isLoading}
              >
                Verificar e Avançar&nbsp;
                <ArrowForwardIcon />
              </MDButton>
            </MDBox>
          </MDBox>
        )}

        {error && (
          <MDBox mt={2}>
            <MDTypography variant="body2" color="error">
              {error}
            </MDTypography>
            <MDButton
              variant="text"
              color="error"
              size="small"
              onClick={handleStartOnboarding}
              sx={{ mt: 1 }}
            >
              {messages.whatsappBspOnboardingLinkRetry}
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}
